import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { from, Observable, Subject } from 'rxjs';
import { filter, first, flatMap, map, mergeMap, reduce, repeat, take, tap } from 'rxjs/operators';
import { CartService } from '../../finance/shared/services/cart.service';
import { getServerDate } from '../../shared/helpers/time-helpers.model';
import { BusinessTransactionWorkQueueDetailModel } from '../../shared/models/business-transaction/business-transaction-work-queue-detail-model';
import { WorkQueueModel } from '../../shared/models/business-transaction/work-queue.model';
import { BusinessTransactionService } from '../../shared/services/business-transaction/business-transaction.service';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { SystemNotificationService } from '../../shared/services/realtime-messaging/system-notification.service';
import { AssignmentBase } from './assignment-base.component';

@Injectable()
export class TransactionQueueDelegate extends AssignmentBase {
    WORKQUEUETYPE_HELPQUEUE = 2;

    public csrOverridesQueue$: Observable<BusinessTransactionWorkQueueDetailModel[]>;
    public levelOneQueue$: Observable<WorkQueueModel[]>;
    public masterDropdownList$: Observable<any[]>;
    public personalQueueData$: Observable<BusinessTransactionWorkQueueDetailModel[]>;
    public tmpQueueData: WorkQueueModel[];
    public userId: number;
    private updateBuQueue$ = new Subject<any>();
    private updateCsrOverrides$ = new Subject<any>();
    private updateLevelOne$ = new Subject<any>();
    private updateMasterDropdown$ = new Subject<any>();

    constructor(
        public cartService: CartService,
        public businessTransactionService: BusinessTransactionService,
        public systemNotificationService: SystemNotificationService,
        public modalService: MvdModalService
    ) {
        super(modalService, systemNotificationService, businessTransactionService);
        this.UseHelpContent('#CONTENT/Cross_Functional/Dashboard.htm%3FTocPath%3DCross%2520Functional%7C_____3');
        this.subscribeDataLoaders();
    }

    getTransactionMetaData(id: any): Promise<any> {
        return this.businessTransactionService.getTransactionMetaData(id).pipe(first()).toPromise();
    }

    loadQueueData() {
        this.updateMasterDropdown$.next(null);
        this.updateBuQueue$.next(null);
        this.updateCsrOverrides$.next(null);
        this.updateLevelOne$.next(null);
    }

    private subscribeDataLoaders() {
        this.masterDropdownList$ = this.updateMasterDropdown$
            .pipe(
                mergeMap(() => this.businessTransactionService.getBusinessUnitUsers()),
                map(data => data.result)
            );

        this.csrOverridesQueue$ = this.updateCsrOverrides$
            .pipe(this.prepareEscalations(this.businessTransactionService.getCsrOverrides));

        this.levelOneQueue$ = this.updateLevelOne$
            .pipe(
                take(1),
                mergeMap(() => this.businessTransactionService.getLevelOneView()),
                flatMap(escalatedTransactionGroups => from(escalatedTransactionGroups)),
                filter(escalatedTransactionGroup => escalatedTransactionGroup.workQueueTypeID === this.WORKQUEUETYPE_HELPQUEUE),
                map(escalatedTransactionGroup => {
                    escalatedTransactionGroup.businessTransactionDetails.forEach(busTransDetail => {
                        if (busTransDetail.updatedDate) {
                            busTransDetail.displayUpdatedDate = moment(busTransDetail.updatedDate).format('lll');
                            busTransDetail.displayElapsedTime = moment(busTransDetail.updatedDate).fromNow();
                        }
                    });
                    return escalatedTransactionGroup;
                }),
                reduce((acc, val) => [...acc, val], []),
                repeat()
            );

        this.personalQueueData$ = this.updateBuQueue$
            .pipe(
                take(1),
                mergeMap(() => this.businessTransactionService.getCurrentUserQueueView()),
                tap(queueView => this.userId = queueView.userId),
                map(queueView => queueView.queueList.map(x => new WorkQueueModel(x))),
                map(workQueues => this.convertDates(workQueues)),
                filter(workQueues => !!workQueues),
                tap(workQueues => this.tmpQueueData = workQueues),
                flatMap(workQueues => from(workQueues)),
                map(workQueue => workQueue.businessTransactionDetails),
                flatMap(businessTransactionDetails => from(businessTransactionDetails)),
                filter(businessTransactionDetail => businessTransactionDetail.assignedTo.toUpperCase() === this.user.userName.toUpperCase()),
                reduce((acc, val) => [...acc, val], []),
                repeat(),
            ) as Observable<BusinessTransactionWorkQueueDetailModel[]>;
    }

    private prepareEscalations = (mergeFn: () => Observable<any[]>) => (source: Observable<any>) => source.pipe(
        take(1),
        mergeMap(() => mergeFn()),
        flatMap(escalatedTransactionGroups => from(escalatedTransactionGroups)),
        map(escalatedTransactionGroup => (escalatedTransactionGroup as any).businessTransactionDetails as BusinessTransactionWorkQueueDetailModel[]),
        filter(busTransDetails => busTransDetails.length > 0),
        flatMap(busTransDetails => from(busTransDetails)),
        map(busTransDetail => {
            if (busTransDetail.updatedDate) {
                const updatedDate = moment(busTransDetail.updatedDate);
                busTransDetail.displayUpdatedDate = updatedDate.format('lll');
                busTransDetail.displayElapsedTime = updatedDate.fromNow();
            }
            return busTransDetail;
        }),
        reduce((acc, val) => [...acc, val], []),
        repeat()
    );

    private convertDates(queues: WorkQueueModel[]) {
        const workQueues = queues.slice(0);
        for (let i = 0; i < workQueues.length; i++) {
            workQueues[i].businessTransactionDetails.forEach(function(x) {
                if (x.customerDOB) {
                    x.displaycustomerDOB = getServerDate(x.customerDOB);
                }

                x.displayUpdatedDate = moment(x.updatedDate).format('lll');
                x.displayElapsedTime = moment(x.updatedDate).fromNow();
            });
        }
        return workQueues;
    }
}
